import { View } from "@pimo/pimo-app-builder";
import {
  DragAndDropLayout,
  PMBarChartCard,
  PMDonutChartCard,
} from "@pimo/pimo-components";
import { ReportingItem } from "vmd-types";

import { TestAppState } from "../../../app";
import { DONUT_CARD_LAYOUT, DONUT_CHART_COLORS } from "../../../constants";
import { withTotalInTitle } from "../with-total-in-title";

const getTechnicalExposureData = (items: ReportingItem[] | undefined) => {
  const exposureCounts = new Map<string, number>();

  for (const item of items ?? []) {
    const exposures = item.basicItem?.technicalExposure ?? [];
    for (const exposure of exposures) {
      if (!exposure) continue;
      exposureCounts.set(exposure, (exposureCounts.get(exposure) ?? 0) + 1);
    }
  }

  const entries = Array.from(exposureCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([label, count], index) => ({
      label,
      count,
      color: DONUT_CHART_COLORS[index % DONUT_CHART_COLORS.length],
    }));

  return { entries };
};

/** Adds the technical-exposure bar chart and donut chart (#2222). */
export function addTechnicalExposureCards(
  view: View<TestAppState, DragAndDropLayout>
) {
  // Technical Exposure
  const technicalExposureBarChartComponent = view.addComponent({
    component: PMBarChartCard,
    layoutProps: {
      componentId: "technicalExposureBarChartComponent",
      labels: ["barChart", "chart", "technicalExposure"],
      description: "Shows systems grouped by technical exposure.",
      title: "Systems per Technical Exposure (Bar Chart)",
    },
  });

  technicalExposureBarChartComponent.mapState(({ currentReport }) => {
    const items = (currentReport?.project?.reportingItems ?? []).filter(
      (item) => !item?.isChannelHeader
    );
    const { entries } = getTechnicalExposureData(items);

    return {
      title: "Systems per Technical Exposure",
      categories: entries.map((entry) => entry.label),
      data: entries.map((entry) => entry.count),
      chartType: 1 as const,
      xLabel: "Systems",
      yLabel: "Technical Exposure",
      colors: entries.map((entry) => entry.color),
      fixedHeight: "320px",
    };
  });

  const technicalExposureDonutChartComponent = view.addComponent({
    component: PMDonutChartCard,
    layoutProps: {
      componentId: "technicalExposureDonutChartComponent",
      labels: ["donutChart", "chart", "technicalExposure"],
      description: "Shows systems grouped by technical exposure.",
      title: "Systems per Technical Exposure",
    },
  });

  technicalExposureDonutChartComponent.mapState(({ currentReport }) => {
    const items = (currentReport?.project?.reportingItems ?? []).filter(
      (item) => !item?.isChannelHeader
    );
    const { entries } = getTechnicalExposureData(items);

    return {
      title: withTotalInTitle("Systems per Technical Exposure", items.length),
      icon: "DonutLarge",
      ...DONUT_CARD_LAYOUT,
      data: entries.map((entry) => ({
        label: entry.label,
        value: entry.count,
        color: entry.color,
      })),
    };
  });
}
